export const OPEN_FIELD_ATTRIBUTE = 'data-open-field';

const OPEN_FIELD_SELECTOR = `[${OPEN_FIELD_ATTRIBUTE}]`;
const CHIP_CLASS =
  'mx-0.5 inline-flex items-center rounded-sm border border-warning-200 bg-warning-50 px-1 font-mono text-xs text-warning-700 data-[selected]:shadow-focus';

/** `<span data-open-field contenteditable="false" tabindex="-1">{text}</span>` in the warning chip style. */
export function createOpenFieldChip(text: string): HTMLSpanElement {
  const chip = document.createElement('span');

  chip.setAttribute(OPEN_FIELD_ATTRIBUTE, '');
  chip.setAttribute('contenteditable', 'false');
  chip.setAttribute('tabindex', '-1');
  chip.className = CHIP_CLASS;
  chip.textContent = text;

  return chip;
}

function isOpenField(node: Node | null | undefined): node is HTMLElement {
  return node instanceof HTMLElement && node.matches(OPEN_FIELD_SELECTOR);
}

/** The chip when the selection is exactly that one node, else null. */
function selectedChip(root: HTMLElement): HTMLElement | null {
  const selection = document.getSelection();

  if (selection === null || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);

  if (range.startContainer !== range.endContainer || !root.contains(range.startContainer)) return null;

  if (range.endOffset - range.startOffset !== 1) return null;

  const node = range.startContainer.childNodes[range.startOffset];

  return isOpenField(node) ? node : null;
}

function replaceWithText(chip: HTMLElement, text: string): void {
  const node = document.createTextNode(text);

  chip.replaceWith(node);

  const range = document.createRange();

  range.setStartAfter(node);
  range.collapse(true);

  const selection = document.getSelection();

  selection?.removeAllRanges();
  selection?.addRange(range);
}

/** Focuses the chip's editable root, selects the chip whole, marks it `data-selected`, scrolls it into view. */
export function selectOpenField(chip: HTMLElement): void {
  const editable = chip.closest<HTMLElement>('[contenteditable="true"], [contenteditable=""]');

  editable?.focus();

  const range = document.createRange();

  range.selectNode(chip);

  const selection = document.getSelection();

  selection?.removeAllRanges();
  selection?.addRange(range);

  for (const other of editable?.querySelectorAll(OPEN_FIELD_SELECTOR) ?? []) other.removeAttribute('data-selected');

  chip.setAttribute('data-selected', '');
  chip.scrollIntoView?.({ block: 'nearest' });
}

/** Click selects a chip; typing / pasting over a selected chip replaces it with text; Backspace / Delete removes it. Returns the detach. */
export function attachOpenFieldInteraction(root: HTMLElement, onEdit: () => void): () => void {
  const onClick = (event: MouseEvent) => {
    const chip = event.target instanceof Element ? event.target.closest<HTMLElement>(OPEN_FIELD_SELECTOR) : null;

    if (chip !== null && root.contains(chip)) selectOpenField(chip);
  };

  const onBeforeInput = (event: InputEvent) => {
    const chip = selectedChip(root);

    if (chip === null) return;

    if (
      event.inputType === 'insertText' ||
      event.inputType === 'insertReplacementText' ||
      event.inputType === 'insertFromPaste'
    ) {
      const text =
        event.inputType === 'insertFromPaste' ? (event.dataTransfer?.getData('text/plain') ?? '') : (event.data ?? '');

      event.preventDefault();
      replaceWithText(chip, text);
      onEdit();

      return;
    }

    if (event.inputType === 'deleteContentBackward' || event.inputType === 'deleteContentForward') {
      event.preventDefault();
      chip.remove();
      onEdit();
    }
  };

  const onSelectionChange = () => {
    const current = selectedChip(root);

    for (const chip of root.querySelectorAll(OPEN_FIELD_SELECTOR)) {
      if (chip !== current) chip.removeAttribute('data-selected');
    }
  };

  root.addEventListener('click', onClick);
  root.addEventListener('beforeinput', onBeforeInput);
  document.addEventListener('selectionchange', onSelectionChange);

  return () => {
    root.removeEventListener('click', onClick);
    root.removeEventListener('beforeinput', onBeforeInput);
    document.removeEventListener('selectionchange', onSelectionChange);
  };
}

/** Every open field once, in document order across `roots` (Betreff first, then Text), plus the first chip node. */
export function collectOpenFields(roots: ReadonlyArray<HTMLElement | null>): {
  readonly labels: ReadonlyArray<string>;
  readonly first: HTMLElement | null;
} {
  const chips = roots.flatMap((root) =>
    root === null ? [] : Array.from(root.querySelectorAll<HTMLElement>(OPEN_FIELD_SELECTOR)),
  );
  const labels = [...new Set(chips.map((chip) => chip.textContent ?? ''))];

  return { labels, first: chips[0] ?? null };
}
